import { A } from "@solidjs/router";
import { createForm } from "@tanstack/solid-form";
import { createEffect, createSignal, Show } from "solid-js";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { useLinkedInProfileErrorMessage } from "@/api/utils/linkedin-error-message";
import { mapProfileSkillWriteError } from "@/api/utils/profile-skill-write-error";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  Input,
} from "@/components/ui/input";
import { usePostHog } from "@/hooks/usePostHog";
import type { CVAnalysis } from "../../api";
import {
  isValidLinkedInUrl,
  normalizeLinkedInUrl,
  useFetchLinkedInProfile,
} from "../../api/linkedin";
import { LinkedInLoadingState } from "./LinkedInLoadingState";

const linkedInUrlSchema = z.object({
  linkedin_url: z
    .string()
    .min(1, "Inserisci un URL LinkedIn")
    .refine(
      (url) => isValidLinkedInUrl(url),
      "Inserisci un URL LinkedIn valido (es: https://www.linkedin.com/in/nome-cognome)",
    ),
});

type LinkedInUrlFormProps = {
  /** `linkedinUrl` is the normalized URL that was actually submitted. */
  onSuccess: (data: CVAnalysis, linkedinUrl: string) => void;
  onError?: (error: string) => void;
  isSelected?: boolean;
  buttonLabel?: string;
  buttonVariant?:
    | "default"
    | "outline"
    | "ghost"
    | "link"
    | "destructive"
    | "secondary";
  showButtonIcon?: boolean;
  autoOpen?: boolean;
};

export const LinkedInUrlInput = (props: LinkedInUrlFormProps) => {
  const { t } = useI18n();
  const getLinkedInProfileErrorMessage = useLinkedInProfileErrorMessage();
  const { capture } = usePostHog();
  const fetchLinkedIn = useFetchLinkedInProfile();
  const [isUrlValid, setIsUrlValid] = createSignal(false);
  const [isOpen, setIsOpen] = createSignal(props.autoOpen ?? false);

  const form = createForm(() => ({
    defaultValues: {
      linkedin_url: "",
    },
    schema: linkedInUrlSchema,
    onSubmit: async ({ value }) => {
      const validatedData = linkedInUrlSchema.safeParse(value);
      if (!validatedData.success) {
        console.error("Validation error", validatedData.error);
        return;
      }

      const normalizedUrl = normalizeLinkedInUrl(
        validatedData.data.linkedin_url,
      );
      capture("start_fetch_linkedin_profile");
      // Reset error state before new fetch
      fetchLinkedIn.reset();
      fetchLinkedIn.mutate({ linkedin_url: normalizedUrl });
    },
  }));

  createEffect(() => {
    if (fetchLinkedIn.isSuccess && fetchLinkedIn.data) {
      capture("end_fetch_linkedin_profile");
      // The submitted URL travels with the analysis so the caller can prefill
      // the profile's LinkedIn field instead of asking for it a second time.
      props.onSuccess(
        fetchLinkedIn.data.analysis,
        fetchLinkedIn.variables.linkedin_url,
      );
    }
  });

  createEffect(() => {
    if (fetchLinkedIn.isError) {
      capture("fail_fetch_linkedin_profile", {
        error_kind: mapProfileSkillWriteError(fetchLinkedIn.error).kind,
      });
      const errorMessage = getLinkedInProfileErrorMessage(
        fetchLinkedIn.error,
        t("onboarding.steps.source.linkedin.error"),
      );
      props.onError?.(errorMessage);
    }
  });

  return (
    <Dialog modal open={isOpen()} onOpenChange={setIsOpen}>
      <DialogTrigger
        as={Button}
        size="lg"
        variant={
          props.buttonVariant ?? (props.isSelected ? "default" : "outline")
        }
        class="w-full flex items-center gap-2"
        onClick={() => {
          capture("click_import_linkedin_url");
        }}
      >
        {props.showButtonIcon && <i class="ph ph-lightning-fill text-lg" />}
        {props.buttonLabel ?? t("onboarding.steps.source.buttons.linkedin")}
      </DialogTrigger>
      <DialogContent class="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {fetchLinkedIn.isPending
              ? t("onboarding.steps.source.linkedin.loading.title")
              : t("onboarding.steps.source.linkedin.title")}
          </DialogTitle>
          <Show when={!fetchLinkedIn.isPending}>
            <DialogDescription>
              {t("onboarding.steps.source.linkedin.description")}
            </DialogDescription>
          </Show>
        </DialogHeader>

        <Show
          when={!fetchLinkedIn.isPending}
          fallback={<LinkedInLoadingState />}
        >
          <form.Field
            name="linkedin_url"
            validators={{
              onChange: linkedInUrlSchema.shape.linkedin_url,
            }}
          >
            {(field) => (
              <FieldContainer>
                <FieldContainerLabel for={field().name}>
                  {t("onboarding.steps.source.linkedin.url_label")}
                </FieldContainerLabel>
                <Input
                  id={field().name}
                  name={field().name}
                  type="url"
                  value={field().state.value ?? ""}
                  onInput={(e) => {
                    const value = e.target.value;
                    field().handleChange(value);
                    // Validate URL in real-time using regex
                    setIsUrlValid(
                      value.trim().length > 0 && isValidLinkedInUrl(value),
                    );
                    // Reset error state when user starts typing
                    if (fetchLinkedIn.isError) {
                      fetchLinkedIn.reset();
                    }
                  }}
                  onBlur={() => field().handleBlur()}
                  placeholder="https://www.linkedin.com/in/nome-cognome"
                  disabled={fetchLinkedIn.isPending}
                  classList={{
                    "border-red-300 focus:border-red-500 focus:ring-red-500/10 bg-red-50/50":
                      fetchLinkedIn.isError,
                  }}
                />
                <FieldInfo field={field()} />
                <A
                  href="https://www.linkedin.com/in/"
                  target="_blank"
                  class="text-primary text-sm font-medium"
                >
                  (Apri LinkedIn)
                  <i class="ph ph-arrow-right ml-2" />
                </A>
              </FieldContainer>
            )}
          </form.Field>

          <Show when={fetchLinkedIn.isError}>
            <div class="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div class="flex items-start gap-3">
                <div class="shrink-0">
                  <i class="ph ph-alert-circle text-red-600 text-xl" />
                </div>
                <div class="flex-1">
                  <p class="text-sm font-medium text-red-800">
                    {t("onboarding.steps.source.linkedin.error_title")}
                  </p>
                  <p class="mt-1 text-sm text-red-600">
                    {getLinkedInProfileErrorMessage(
                      fetchLinkedIn.error,
                      t("onboarding.steps.source.linkedin.error"),
                    )}
                  </p>
                </div>
              </div>
            </div>
          </Show>

          <div class="mt-6 flex items-center justify-between gap-2">
            <DialogClose>
              <Button
                variant="secondary"
                class="w-fit"
                onClick={() => {
                  // Reset error state when closing dialog
                  if (fetchLinkedIn.isError) {
                    fetchLinkedIn.reset();
                  }
                }}
              >
                {t("common.close")}
              </Button>
            </DialogClose>
            <form.Subscribe
              selector={(state) => ({
                isValid: state.isValid,
              })}
            >
              {() => (
                <div class="flex items-center gap-2">
                  <Show when={fetchLinkedIn.isError}>
                    <Button
                      variant="outline"
                      onClick={() => {
                        // Reset error and retry
                        fetchLinkedIn.reset();
                        form.handleSubmit();
                      }}
                      disabled={!isUrlValid() || fetchLinkedIn.isPending}
                    >
                      {t("common.retry")}
                    </Button>
                  </Show>
                  <Button
                    onClick={() => form.handleSubmit()}
                    disabled={!isUrlValid() || fetchLinkedIn.isPending}
                  >
                    {fetchLinkedIn.isPending
                      ? t("common.loading")
                      : t("onboarding.steps.source.linkedin.import")}
                  </Button>
                </div>
              )}
            </form.Subscribe>
          </div>
        </Show>
      </DialogContent>
    </Dialog>
  );
};
